import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import api from '../services/api';

// Light theme colors
const lightTheme = {
  mode: 'light',
  colors: {
    // Background colors
    background: '#f9fafb',
    backgroundSecondary: '#ffffff',
    backgroundTertiary: '#f3f4f6',
    
    // Text colors
    text: '#111827',
    textSecondary: '#4b5563',
    textTertiary: '#6b7280',
    textMuted: '#9ca3af',
    
    // Primary colors (red scheme)
    primary: '#dc2626',
    primaryLight: '#fef2f2',
    primaryDark: '#b91c1c',
    primaryHover: '#ef4444',
    
    // Border colors
    border: '#e5e7eb',
    borderLight: '#f3f4f6',
    borderDark: '#d1d5db',
    
    // Status colors
    success: '#22c55e',
    successLight: '#f0fdf4',
    warning: '#eab308',
    warningLight: '#fefce8',
    error: '#ef4444',
    errorLight: '#fef2f2',
    info: '#3b82f6',
    infoLight: '#eff6ff',
    
    // Interactive colors
    inputBackground: '#ffffff',
    inputBorder: '#d1d5db',
    inputFocus: '#dc2626',
    
    // Card colors
    cardBackground: '#ffffff',
    cardBorder: '#e5e7eb',
    cardShadow: 'rgba(0, 0, 0, 0.1)',
    
    // Sidebar colors
    sidebarBackground: '#1f2937',
    sidebarText: '#ffffff',
    sidebarTextSecondary: '#9ca3af',
    sidebarHover: '#374151',
    sidebarActive: '#dc2626',
    
    // Header colors
    headerBackground: '#ffffff',
    headerBorder: '#e5e7eb',
    
    // Table colors
    tableHeader: '#f9fafb',
    tableRow: '#ffffff',
    tableRowHover: '#f3f4f6',
    tableRowAlt: '#fafafa',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
};

// Dark theme colors
const darkTheme = {
  mode: 'dark',
  colors: {
    // Background colors
    background: '#111827',
    backgroundSecondary: '#1f2937',
    backgroundTertiary: '#374151',
    
    // Text colors
    text: '#ffffff',
    textSecondary: '#e5e7eb',
    textTertiary: '#d1d5db',
    textMuted: '#9ca3af',
    
    // Primary colors (red scheme)
    primary: '#ef4444',
    primaryLight: '#7f1d1d',
    primaryDark: '#dc2626',
    primaryHover: '#f87171',
    
    // Border colors
    border: '#374151',
    borderLight: '#4b5563',
    borderDark: '#1f2937',
    
    // Status colors
    success: '#22c55e',
    successLight: '#14532d',
    warning: '#eab308',
    warningLight: '#713f12',
    error: '#ef4444',
    errorLight: '#7f1d1d',
    info: '#3b82f6',
    infoLight: '#1e3a8a',
    
    // Interactive colors
    inputBackground: '#374151',
    inputBorder: '#4b5563',
    inputFocus: '#ef4444',
    
    // Card colors
    cardBackground: '#1f2937',
    cardBorder: '#374151',
    cardShadow: 'rgba(0, 0, 0, 0.3)',
    
    // Sidebar colors
    sidebarBackground: '#0f172a',
    sidebarText: '#ffffff',
    sidebarTextSecondary: '#d1d5db',
    sidebarHover: '#1e293b',
    sidebarActive: '#ef4444',
    
    // Header colors
    headerBackground: '#1f2937',
    headerBorder: '#374151',
    
    // Table colors
    tableHeader: '#374151',
    tableRow: '#1f2937',
    tableRowHover: '#374151',
    tableRowAlt: '#263344',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
  },
};

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Get system preference
const getSystemTheme = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

export const ThemeProvider = ({ children }) => {
  const [themePreference, setThemePreference] = useState('system');
  const [activeTheme, setActiveTheme] = useState('light');
  const [loading, setLoading] = useState(true);

  // Compute the actual theme based on preference
  const computeActiveTheme = useCallback((preference) => {
    if (preference === 'system') {
      return getSystemTheme();
    }
    return preference;
  }, []);

  // Fetch theme preference from API
  useEffect(() => {
    const fetchThemePreference = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await api.get('/preferences/theme');
          const preference = response.data.theme_preference || 'system';
          setThemePreference(preference);
          setActiveTheme(computeActiveTheme(preference));
        } else {
          // Use stored preference or system default
          const stored = localStorage.getItem('theme_preference') || 'system';
          setThemePreference(stored);
          setActiveTheme(computeActiveTheme(stored));
        }
      } catch (error) {
        console.error('Failed to fetch theme preference:', error);
        const stored = localStorage.getItem('theme_preference') || 'system';
        setThemePreference(stored);
        setActiveTheme(computeActiveTheme(stored));
      } finally {
        setLoading(false);
      }
    };

    fetchThemePreference();
  }, [computeActiveTheme]);

  // Listen for system theme changes
  useEffect(() => {
    if (themePreference === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        setActiveTheme(e.matches ? 'dark' : 'light');
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themePreference]);

  // Update theme preference
  const setTheme = async (preference) => {
    setThemePreference(preference);
    setActiveTheme(computeActiveTheme(preference));
    localStorage.setItem('theme_preference', preference);

    try {
      const token = localStorage.getItem('token');
      if (token) {
        await api.put('/preferences/theme', { theme_preference: preference });
      }
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  };

  // Toggle between light and dark
  const toggleTheme = () => {
    const newTheme = activeTheme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  const theme = activeTheme === 'dark' ? darkTheme : lightTheme;

  const value = {
    themePreference,
    activeTheme,
    setTheme,
    toggleTheme,
    isDark: activeTheme === 'dark',
    isLight: activeTheme === 'light',
    loading,
  };

  return (
    <ThemeContext.Provider value={value}>
      <StyledThemeProvider theme={theme}>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export { lightTheme, darkTheme };
